import React, { useState, useEffect, useRef, useCallback } from 'react';

interface PlaceSearchProps {
  onPlaceSelect: (lat: number, lng: number, address: string) => void;
  placeholder?: string;
  currentLocation?: { latitude: number; longitude: number; address?: string } | null;
  disabled?: boolean;
}

interface SearchResult {
  display_name: string;
  lat: string;
  lon: string;
}

const PlaceSearch: React.FC<PlaceSearchProps> = ({ onPlaceSelect, placeholder = 'Search for a place...', currentLocation, disabled = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const performSearch = useCallback(async (query: string) => {
    try {
      // Wait for Google Maps to load
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        // Retry after a short delay
        setTimeout(() => performSearch(query), 100);
        return;
      }

      const service = new google.maps.places.PlacesService(document.createElement('div'));
      const request: google.maps.places.PlaceSearchRequest = {
        query: query,
        fields: ['formatted_address', 'geometry', 'name'],
      };

      service.textSearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          const searchResults: SearchResult[] = results.map((place) => ({
            display_name: place.formatted_address || place.name || '',
            lat: place.geometry?.location?.lat().toString() || '0',
            lon: place.geometry?.location?.lng().toString() || '0',
          }));
          setResults(searchResults);
          setShowResults(true);
        } else {
          setResults([]);
        }
        setIsSearching(false);
      });
    } catch (error) {
      console.error('Error searching places:', error);
      setResults([]);
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  const handleSelectPlace = (result: SearchResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    onPlaceSelect(lat, lng, result.display_name);
    setSearchQuery('');
    setResults([]);
    setShowResults(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleInputFocus = () => {
    if (results.length > 0) {
      setShowResults(true);
    }
  };

  return (
    <div ref={searchRef} style={{ position: 'relative', marginBottom: '8px' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="search-input"
          disabled={disabled}
          style={{
            width: '100%',
            padding: '10px 40px 10px 12px',
            borderRadius: '6px',
            fontSize: '16px',
            boxSizing: 'border-box',
          }}
        />
        {isSearching && (
          <span
            className="place-search-loading"
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '14px',
            }}
          >
            Searching...
          </span>
        )}
        {!isSearching && searchQuery && (
          <button
            type="button"
            onClick={() => {
              setSearchQuery('');
              setResults([]);
              setShowResults(false);
            }}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              color: '#999',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {results.map((result, index) => (
            <div
              key={index}
              onClick={() => handleSelectPlace(result)}
              style={{
                padding: '12px',
                cursor: 'pointer',
                borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <div style={{ fontSize: '16px', fontWeight: '500', color: '#333' }}>
                {result.display_name.split(',')[0]}
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
                {result.display_name}
              </div>
            </div>
          ))}
        </div>
      )}

      {showResults && results.length === 0 && searchQuery && !isSearching && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            padding: '12px',
            fontSize: '16px',
            color: '#666',
          }}
        >
          No places found
        </div>
      )}
    </div>
  );
};

export default PlaceSearch;

